import React, { useCallback, useState } from "react";
import { Button, Grid, Typography } from "@material-ui/core";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { CancelButton, SaveButton } from "src/components/Buttons";
import { useTranslation } from "react-i18next";
import ServiceFieldsRow from "./components/ServiceFields/ServiceFieldsRow";
import { Services, ServicesReq } from "src/providers/SODetailsStore/types";
import { useSODetailsContext } from "src/providers/SODetailsStore";
import Success from "./components/Success/";
import { useAppContext } from "src/providers/AppProvider";
import Modal from "src/components/Modal";
import { useStyles } from "./styles";
import PlusIcon from "./icons/PlusIcon";
import { __ } from "lodash/fp";
import { omit } from "lodash";
import { useSOContext } from "src/providers/ServiceOrdersStore";
import { useParams } from "react-router-dom";
import { SODetailsType } from "../../type";

const CreateServiceInBulk: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation("orders");
  const { serviceOrderId } = useParams<SODetailsType>();
  const {
    serviceOrderDetails: { serviceOrder },
    callAPI: { setContextData, createServiceInBulk, getRelatedServices, getRelatedServiceOrders },
    getServiceDefaultDeliveryDate,
  } = useSODetailsContext();
  const {
    SOData: { responseObject },
  } = useSOContext();
  const { startDate } = serviceOrder;
  const initServiceState = {
    serviceTypeId: undefined,
    startDate,
    deliveryDate: getServiceDefaultDeliveryDate(),
    capacity: null,
  };
  const [success, setSuccess] = useState<boolean>(false);
  const redirectToServicesTab =
    (Number(responseObject?.id) || Number(serviceOrder.id)) === Number(serviceOrderId);
  const {
    state: {
      modals: { createServiceBulkModal },
    },
    callAppAPI,
  } = useAppContext();

  const methods = useForm<ServicesReq>({
    mode: "onChange",
    defaultValues: {
      services: [initServiceState],
    },
  });
  const { handleSubmit, control, watch } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "services",
  });

  const disableDelete: boolean = watch().services.length === 1;

  const disableSubmit: boolean = watch()
    .services.map((fieldsObject) =>
      Object.values(omit(fieldsObject, "capacity")).some((el) => __.isEmpty(el))
    )
    .includes(true);

  const addNewRow = () => append(initServiceState);
  const removeRow = (index: number) => {
    remove(index);
  };
  const onSubmit = (data: ServicesReq) => {
    if (data.services && data.services.length > 0 && serviceOrder?.id && serviceOrder?.owner?.key) {
      const parsedReqData = data.services.map(
        (el): Services => {
          // Add required info
          return {
            ...el,
            ownerId: Number(responseObject?.owner?.key) || Number(serviceOrder?.owner?.key),
            serviceOrderId: Number(responseObject?.id) || Number(serviceOrder.id),
          };
        }
      );
      createServiceInBulk(parsedReqData)
        .then((success) => {
          // Submit services collection
          setContextData("servicesCreted", true);
          setSuccess(success);
        })
        .then(() => {
          if (redirectToServicesTab) getRelatedServices(Number(serviceOrder.id));
          else getRelatedServiceOrders(Number(serviceOrder.id));
        });
    }
  };
  const handleModalClose = useCallback(() => {
    callAppAPI.toggleModal({ id: "createServiceBulkModal", status: false });
    setSuccess(false);
  }, []);

  return (
    <>
      {!success && (
        <Modal isOpen={createServiceBulkModal} onClose={handleModalClose} maxWidth="md">
          <Grid container>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
                <Typography variant="h3">{t("create_services_in_bulk_form.title")}</Typography>
                <br />
                {!!fields.length && (
                  <div className={classes.container}>
                    <Typography variant="subtitle1" color="textSecondary">
                      {t("create_services_in_bulk_form.service_type")}
                    </Typography>
                    <Typography variant="subtitle1" color="textSecondary">
                      {t("create_services_in_bulk_form.start_date")}
                    </Typography>
                    <Typography variant="subtitle1" color="textSecondary">
                      {t("create_services_in_bulk_form.delivery_date")}
                    </Typography>
                    <Typography variant="subtitle1" color="textSecondary">
                      {t("labels.creation.service.capacity")}
                    </Typography>
                    <div />
                    {fields.map((item, index) => {
                      return (
                        <ServiceFieldsRow
                          key={item.id}
                          item={item}
                          index={index}
                          removeRow={removeRow}
                          disableDelete={disableDelete}
                        />
                      );
                    })}
                  </div>
                )}
                <div>
                  <Button
                    variant="text"
                    size="large"
                    className={classes.addMoreButton}
                    startIcon={<PlusIcon />}
                    onClick={addNewRow}
                  >
                    <Typography color="primary" style={{ color: "#2B59C3" }}>
                      Add more
                    </Typography>
                  </Button>
                </div>
                <Grid container wrap="nowrap" alignItems="center">
                  <SaveButton
                    disabled={disableSubmit}
                    label={t("labels.save", { ns: "common" })}
                    type="submit"
                  />
                  <CancelButton
                    label={t("labels.cancel", { ns: "common" })}
                    onClick={handleModalClose}
                  />
                </Grid>
              </form>
            </FormProvider>
          </Grid>
        </Modal>
      )}
      {/* Display success message */}
      <Success open={success} redirectToServicesTab={redirectToServicesTab} />
    </>
  );
};

export default CreateServiceInBulk;
